import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Container, Row, Col } from "react-bootstrap";
import Typewriter from "typewriter-effect";
import { introdata, meta } from "../../content_option";
import { Link } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";
import me from "../../assets/images/me.jpg";
import { Github, Instagram, Linkedin, Loader, Twitter } from "lucide-react";
import timepulse from "../../assets/screenshots/timepulse/timepulse.png";
import election from "../../assets/screenshots/mob/mobicrypto.png";
import winforlux from "../../assets/screenshots/wiforLux/win4lux.png";
// Separate CSS file
import "./style.css";
import Skills from "components/Home/Skills";
import Services from "components/Home/Services";
import MarqueeProjects from "components/Home/Marquee";

// Portfolio projects data (you can move this to content_option.js)
const portfolioProjects = [
  {
    title: "Timepulse (Web & Desktop)",
    description:
      "A full-featured productivity tracking system inspired by Hubstaff, including desktop app and web dashboard with time tracking, screenshot capturing, activity monitoring, and user management.",
    isSignature: false, // This project is marked as the signature product
    img: timepulse,
    link: "https://trackhives.netlify.app/auth/login",
    techStack: [
      "React",
      "Electron.js",
      "Node.js",
      "MongoDB",
      "Firebase",
      "Socket.IO",
    ],
    installation: "npm install\nnpm start",
    previewLink: "https://trackhives.netlify.app/auth/login",
    githubLink: "https://github.com/user/hubstaff-clone",
  },
  {
    title: "Mobicrypto",
    description:
      "A unilevel and Binary Crypto mining website, where user can earn different tpes of commission and coinpayments integrated",
    img: election,
    link: "https://n-mobicrypto.netlify.app/auth/login",
    techStack: [
      "React",
      "Node.js",
      "Express",
      "MongoDB",
      "JWT",
      "Coinpayments",
      "MUI",
    ],
    installation: "npm install\nnpm run dev",
    previewLink: "https://n-mobicrypto.netlify.app/auth/login",
    githubLink: "https://github.com/user/election-management",
  },
  {
    title: "Win4Lux",
    description:
      "Multi-level marketing website with level-wise commission tracking, tree graph visualizations, and detailed reports. Includes complete user profile management: Paypal , Coinpayments integrated",
    img: winforlux,
    isSignature: true,
    link: "https://mlm-level-system.com",
    techStack: [
      "React",
      "Node.js",
      "MongoDB",
      "Express js",
      "MUI",
      "Tailiwind",
      "JWT",
      "Paypal",
      "Nowpayments",
    ],
    installation: "npm install\nnpm run dev",
    previewLink: "https://win4lux.netlify.app/",
    githubLink: "https://github.com/user/mlm-system",
  },
];

// Footer Component
const Footer = () => {
  return (
    <footer className="home-footer">
      <Container>
        <Row className="home-footer-row">
          <Col md={4} className="home-footer-brand">
            <div className="home-footer-profile">
              <img src={me} alt="Profile" className="home-footer-image" />
              <span className="home-footer-name">{introdata.name}</span>
            </div>
            <p className="home-footer-tagline">{introdata.description}</p>
          </Col>
          <Col md={4} className="home-footer-links">
            <h4 className="home-footer-heading">Quick Links</h4>
            <ul className="home-footer-list">
              <li>
                <Link to="/" className="home-footer-link">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="home-footer-link">
                  Portfolio
                </Link>
              </li>
              <li>
                <Link to="/about" className="home-footer-link">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" className="home-footer-link">
                  Contact
                </Link>
              </li>
            </ul>
          </Col>
          <Col md={4} className="home-footer-social">
            <h4 className="home-footer-heading">Connect With Me</h4>
            <div className="home-footer-icons">
              <a
                href="https://github.com/Nouman475"
                className="home-footer-icon"
              >
                <Github size={20} />
              </a>
              <a
                href="https://www.linkedin.com/in/noumanthedeveloper/"
                className="home-footer-icon"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://www.instagram.com/noumanthedeveloper/"
                className="home-footer-icon"
              >
                <Instagram size={20} />
              </a>
            </div>
            <p className="home-footer-copyright">
              &copy; {new Date().getFullYear()} {introdata.name}. All rights
              reserved.
            </p>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export const Home = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="loader-container">
        <Loader className="spinner" size={48} />
      </div>
    );
  }

  const signatureProject = portfolioProjects.find(
    (project) => project.isSignature
  );
  const otherProjects = portfolioProjects.filter(
    (project) => !project.isSignature
  );

  return (
    <HelmetProvider>
      <div className="home-page-wrapper">
        <Helmet>
          <meta charSet="utf-8" />
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>

        {/* Enhanced Hero Section with Glowing Grid */}
        <section className="home-hero-section">
          <div className="home-hero-grid-background">
            <div className="home-grid-container">
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "10%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "20%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "30%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "40%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "50%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "60%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "70%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "80%" }}
              ></div>
              <div
                className="home-grid-line home-grid-vertical"
                style={{ left: "90%" }}
              ></div>

              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "10%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "20%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "30%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "40%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "50%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "60%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "70%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "80%" }}
              ></div>
              <div
                className="home-grid-line home-grid-horizontal"
                style={{ top: "90%" }}
              ></div>
            </div>

            {/* Floating Orbs */}
            <div className="home-floating-orb home-orb-1"></div>
            <div className="home-floating-orb home-orb-2"></div>
            <div className="home-floating-orb home-orb-3"></div>
            <div className="home-floating-orb home-orb-4"></div>
          </div>

          <div className="home-hero-overlay"></div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Container>
              <div className="home-hero-content mt-5">
                <h1 className="home-hero-title">
                  Hi, I'm{" "}
                  <span className="home-highlight">{introdata.name}</span>
                </h1>

                <h2 className="home-hero-subtitle">
                  I'm a{" "}
                  <span className="home-typewriter">
                    <Typewriter
                      options={{
                        strings: [
                          introdata.animated.first,
                          introdata.animated.second,
                          introdata.animated.third,
                        ],
                        autoStart: true,
                        loop: true,
                        deleteSpeed: 10,
                        delay: 50,
                      }}
                    />
                  </span>
                </h2>

                {/* <p className="home-hero-description">{introdata.description}</p> */}

                <div className="home-hero-cta">
                  <Link to="/portfolio" className="home-btn home-btn-primary">
                    <span>View My Work</span>
                    <ArrowRightOutlined />
                  </Link>
                  <Link to="/services" className="home-btn home-btn-secondary">
                    View Services
                  </Link>
                </div>
              </div>
            </Container>
            <MarqueeProjects />
          </div>
        </section>

        {/* About Section */}
        <section className="home-about-section">
          <Container>
            <Row className="align-items-center">
              <Col lg={6}>
                <div className="home-about-content">
                  <h2 className="home-section-title">About Me</h2>
                  <p className="home-about-text">
                    As a skilled MERN stack developer, I excel in creating
                    robust and scalable web applications alongwith android React
                    Native Apps, with expertise extending to front-end
                    development with Bootstrap, Tailwind CSS, Saas, and Scss,
                  </p>
                  <p className="home-about-text">
                    back-end development with Node.js, Express, and MongoDB, and
                    real-time database management with Google Firebase and
                    Firestore.
                  </p>
                  <div className="home-skills">
                    <h3 className="home-skills-title">My Skills</h3>
                    <div className="home-skills-list">
                      <span className="home-skill-badge">MERN stack</span>
                      <span className="home-skill-badge">Mobile Apps</span>
                      <span className="home-skill-badge">
                        Project Management
                      </span>
                      <span className="home-skill-badge">SAAS</span>
                      <span className="home-skill-badge">Problem Solving</span>
                      <span className="home-skill-badge">Git & GitHub</span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <div className="home-about-stats">
                  <div
                    className="home-stat-item"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(15, 14, 14, 0.45), rgba(0,0,0,.45)), url("https://images.stockcake.com/public/e/e/6/ee6071b7-ef42-45e4-ba8c-9fc44bbdfa22_large/coding-at-night-stockcake.jpg")',
                    }}
                  >
                    <div className="home-stat-number">50+</div>
                    <div className="home-stat-label">Projects Completed</div>
                  </div>
                  <div
                    className="home-stat-item"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45)), url("https://www.shutterstock.com/image-photo/hands-typing-on-laptop-programming-600nw-2480023489.jpg")',
                    }}
                  >
                    <div className="home-stat-number">2+</div>
                    <div className="home-stat-label">Years Experience</div>
                  </div>
                  <div
                    className="home-stat-item"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45)), url("https://images.unsplash.com/photo-1526925539332-aa3b66e35444?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZnVsbCUyMHN0YWNrJTIwZGV2ZWxvcGVyfGVufDB8fDB8fHww")',
                    }}
                  >
                    <div className="home-stat-number">10+</div>
                    <div className="home-stat-label">MERN projects</div>
                  </div>
                  <div
                    className="home-stat-item"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45)), url("https://png.pngtree.com/thumb_back/fh260/background/20230705/pngtree-3d-rendering-of-blue-code-sentences-on-a-black-screen-image_3803403.jpg")',
                    }}
                  >
                    <div className="home-stat-number">3+</div>
                    <div className="home-stat-label">Mobile Apps</div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Portfolio Showcase */}
        <section className="home-portfolio-section">
          <Container>
            <div className="home-section-header">
              <h2 className="home-section-title">Featured Projects</h2>
              <p className="home-section-subtitle">Some of my recent work</p>
            </div>
            <Row>
              {signatureProject && (
                <Col md={12} className="home-signature-project-col">
                  <div className="home-signature-project-card">
                    <div className="home-signature-project-content">
                      <h3 className="home-project-title">
                        {signatureProject.title}
                      </h3>
                      <p className="home-project-description">
                        {signatureProject.description}
                      </p>
                      <div className="home-signature-tech-stack">
                        {signatureProject.techStack.map((tech, index) => (
                          <span key={index} className="home-signature-badge">
                            {tech}
                          </span>
                        ))}
                      </div>
                      <a
                        href={signatureProject.previewLink}
                        className="home-project-link home-signature-link"
                        target="_blank"
                      >
                        Explore Project <ArrowRightOutlined />
                      </a>
                    </div>
                    <div className="home-signature-project-image-wrapper">
                      <div className="home-signature-project-image">
                        <img
                          src={signatureProject.img}
                          alt={signatureProject.title}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              )}
              {otherProjects.map((project, index) => (
                <Col md={6} key={index} className="home-project-col mt-3">
                  <div className="home-project-card">
                    <div className="home-project-image">
                      <img src={project.img} alt={project.title} />
                    </div>
                    <div className="home-project-content">
                      <h3 className="home-project-title">{project.title}</h3>
                      <p className="home-project-description">
                        {project.description}
                      </p>
                      <a
                        href={project.link}
                        target="_blank"
                        className="home-project-link"
                      >
                        View <ArrowRightOutlined />
                      </a>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
            <div className="home-portfolio-cta">
              <Link to="/portfolio" className="home-btn home-btn-outline">
                View All Projects
              </Link>
            </div>
          </Container>
        </section>
        <section>
          <Skills />
        </section>
        <section>
          <Services />
        </section>

        {/* CTA Section */}
        <section className="home-cta">
          <div className="home-cta-overlay"></div>
          <div className="home-cta-particles">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
          </div>
          <Container>
            <div className="home-cta-content">
              <h2 className="home-cta-title">
                Ready to turn your vision into reality?
              </h2>
              <p className="home-cta-text">
                Let's collaborate and create something truly impactful.
                <br />
                I'm available for new projects and opportunities.
              </p>
              <Link to="/contact" className="home-cta-btn">
                <span>Get In Touch</span>
                <div className="home-cta-btn-ripple"></div>
              </Link>
            </div>
          </Container>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </HelmetProvider>
  );
};
